import { useRef, useState, useMemo, useEffect } from 'react'
import {
  Box,
  Button,
  Card,
  CardHeader,
  CardBody,
  Heading,
  HStack,
  VStack,
  Text,
  Input,
  InputGroup,
  InputRightElement,
  Select,
  Tabs,
  TabList,
  Tab,
  Badge,
  IconButton,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from '@chakra-ui/react'
import { Search, Check } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { AdminLayout } from '@/components/layout/AdminLayout'
import { DataTable, type DataTableRef, type DataTableOptions } from '@/components/tables'
import { ActionButtons } from '@/components/common/ActionButtons'
import { useToastNotification } from '@/lib/utils/useToastNotification'
import { useAccessCheck } from '@/hooks/useAccess'
import { apiClient } from '@/lib/api/client'
import { useThemeColors } from '@/hooks/useThemeColors'
import { createNameRenderer } from '@/lib/utils/tableRenderers'

type TabValue = 'all' | 'trash'

export default function RatePerClientPage() {
  const colors = useThemeColors()
  const navigate = useNavigate()
  const tableRef = useRef<DataTableRef>(null)
  const searchRef = useRef<HTMLInputElement>(null)
  const { showError, showSuccess, showWarning } = useToastNotification()
  const { isOpen, onOpen, onClose } = useDisclosure()

  const [activeTab, setActiveTab] = useState<TabValue>('all')
  const [searchQuery, setSearchQuery] = useState('')
  const [totalRows, setTotalRows] = useState(0)
  const [totalTrash, setTotalTrash] = useState(0)
  const [bulkAction, setBulkAction] = useState('')
  const [isBulkProcessing, setIsBulkProcessing] = useState(false)
  const [modalMessage, setModalMessage] = useState('')

  const access = useAccessCheck()

  const tableOptions: DataTableOptions = useMemo(
    () => ({
      dataFields: {
        shipping_line: {
          name: 'Shipping Line',
          withSort: true,
          maxWidth: '200px',
          // Use backend column for sorting
          sortField: 'shipping_line_id',
        },
        no_of_days: {
          name: 'No. of Days',
          withSort: true,
          maxWidth: '100px',
        },
        requirements: {
          name: 'Requirements',
          maxWidth: '200px',
        },
        remarks: {
          name: 'Remarks',
          maxWidth: '200px',
        },
        cypa: {
          name: 'Container Yard',
          withSort: true,
          maxWidth: '200px',
          // Use backend column for sorting
          sortField: 'cypa_id',
        },
        container_size: {
          name: 'Size',
          withSort: true,
          maxWidth: '150px',
        },
        stack_run: {
          name: 'Stack Run',
          withSort: true,
          maxWidth: '100px',
        },
        rate: {
          name: 'Rate',
          withSort: true,
          maxWidth: '100px',
        },
        has_vat: {
          name: 'Has VAT',
          maxWidth: '50px',
        },
      },
      dataSource: activeTab === 'trash' ? '/archived/rate-per-clients' : '/rate-per-clients',
      primaryKey: 'id',
      softDelete: true,
      displayInModal: false,
      noRecordText: 'No client rate found',
      otherActions: undefined,
      editPath: '/rates-and-expenses/rate-per-client',
    }),
    [activeTab]
  )

  const permissions = useMemo(
    () => ({
      can_edit: access.can_edit,
      can_delete: access.can_delete,
      can_create: access.can_create,
      can_view: access.can_view,
      can_export: false,
      can_import: false,
    }),
    [access]
  )

  const showSubSubCallback = useMemo(
    () => (all: number, trashed: number) => {
      setTotalRows(all)
      setTotalTrash(trashed)
    },
    []
  )

  const tableParams = useMemo(() => ({ search: searchQuery }), [searchQuery])

  const handleTabChange = (index: number) => {
    setActiveTab(index === 0 ? 'all' : 'trash')
    setSearchQuery('')
    if (searchRef.current) {
      searchRef.current.value = ''
    }
    tableRef.current?.clearPage()
  }

  const handleSearch = () => {
    const query = searchRef.current?.value || ''
    setSearchQuery(query)
    tableRef.current?.applyFilter('search', query)
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch()
    }
  }

  const handleBulkActionClick = () => {
    if (!bulkAction) {
      showWarning('Please select an action first')
      return
    }
    const selectedIds = tableRef.current?.getSelectedRows() || []
    if (selectedIds.length === 0) {
      showWarning('Please select at least one item')
      return
    }

    if (bulkAction === 'delete' && activeTab === 'trash') {
      setModalMessage('You are about to permanently delete these items from your site. This action cannot be undone.')
    } else {
      setModalMessage('Are you sure to apply this change?')
    }
    onOpen()
  }

  const handleConfirmBulkAction = async () => {
    const selectedIds = tableRef.current?.getSelectedRows() || []
    if (selectedIds.length === 0) return

    setIsBulkProcessing(true)
    try {
      let url = ''
      let payload: any = { ids: selectedIds }
      let successMessage = 'Action completed successfully'

      if (bulkAction === 'restore') {
        url = '/rate-per-clients/bulk/restore'
        successMessage = 'Rate per clients restored successfully'
      } else if (bulkAction === 'delete') {
        url =
          activeTab === 'trash'
            ? '/rate-per-clients/bulk/force-delete'
            : '/rate-per-clients/bulk/delete'
        successMessage =
          activeTab === 'trash'
            ? 'Rate per clients permanently deleted successfully'
            : 'Rate per clients moved to trash successfully'
      } else {
        showWarning('Unsupported bulk action')
        setIsBulkProcessing(false)
        return
      }

      await apiClient.post(url, payload)
      showSuccess('Success', successMessage)
      tableRef.current?.reload()
      setBulkAction('')
      onClose()
    } catch (error: any) {
      showError(error)
    } finally {
      setIsBulkProcessing(false)
    }
  }

  // Auto-switch to All tab when Trash becomes empty
  useEffect(() => {
    if (activeTab === 'trash' && totalTrash === 0) {
      // Switch to All tab and refresh
      setActiveTab('all')
      setSearchQuery('')
      if (searchRef.current) {
        searchRef.current.value = ''
      }
      tableRef.current?.clearPage()
      // Small delay to ensure tab switch completes before reload
      setTimeout(() => {
        tableRef.current?.reload()
      }, 100)
    }
  }, [totalTrash, activeTab])

  const customRenderers = useMemo(
    () => ({
      shipping_line: createNameRenderer(true),
      cypa: (value: any, row?: any) => {
        // If cypa is not null, display cypa.name
        if (value !== null && value !== undefined) {
          return createNameRenderer(true)(value)
        }
        
        // If cypa is null, check cypa_id
        const cypaId = row?.cypa_id
        if (cypaId === 0 || cypaId === '0') {
          return (
            <Box
              maxW="200px"
              wordBreak="break-word"
              whiteSpace="normal"
              overflowWrap="break-word"
            >
              All CY
            </Box>
          )
        }
        
        // If cypa_id is not 0, display empty string
        return ''
      },
      no_of_days: (value: any) => {
        if (value === null || value === undefined || value === '') {
          return ''
        }

        // Normalize to number if possible
        const numeric =
          typeof value === 'number'
            ? value
            : typeof value === 'string'
            ? Number(value)
            : NaN

        if (Number.isNaN(numeric)) {
          return ''
        }

        const suffix = numeric === 1 ? 'day' : 'days'
        return `${numeric} ${suffix}`
      },
      requirements: (value: any) => {
        return (
          <Box
            maxW="200px"
            wordBreak="break-word"
            whiteSpace="normal"
            overflowWrap="break-word"
          >
            {value || ''}
          </Box>
        )
      },
      remarks: (value: any) => {
        return (
          <Box
            maxW="200px"
            wordBreak="break-word"
            whiteSpace="normal"
            overflowWrap="break-word"
          >
            {value || ''}
          </Box>
        )
      },
      has_vat: (value: any) => {
        if (value === true) {
          return (
            <Box display="inline-flex" color="green.500">
              <Check size={20} />
            </Box>
          )
        }
        return ''
      },
    }),
    []
  )

  return (
    <AdminLayout>
      <Box p={4} h="100%" overflow="hidden" display="flex" flexDirection="column">
        <VStack spacing={2} align="stretch" flex="1" overflow="hidden" minH={0}>
          <Card flexShrink={0} display="flex" flexDirection="column" flex="1" minH={0} overflow="hidden">
            <CardHeader flexShrink={0} py={3}>
              <Heading size="md" mb={1}>
                Rate Per Client
              </Heading>
              <Text fontSize="sm" color={colors.text}>
                Manage rate per client records
              </Text>
            </CardHeader>

            <CardHeader py={0} px={0} flexShrink={0}>
              <Box pt={2} pb={0}>
                <HStack justify="space-between" align="flex-end" spacing={4} px={4} width="100%">
                  <Tabs index={activeTab === 'all' ? 0 : 1} onChange={handleTabChange}>
                    <TabList borderBottom="none">
                      <Tab>
                        All{' '}
                        <Badge ml={2} colorScheme="blue">
                          {totalRows}
                        </Badge>
                      </Tab>
                      {totalTrash > 0 && (
                        <Tab>
                          Trash{' '}
                          <Badge ml={2} colorScheme="red">
                            {totalTrash}
                          </Badge>
                        </Tab>
                      )}
                    </TabList>
                  </Tabs>
                  <Box pb={2}>
                    <ActionButtons
                      pathname="/rates-and-expenses/rate-per-client"
                      createLabel="Add Rate per Client"
                    />
                  </Box>
                </HStack>
                <Box borderBottom="2px" borderColor={colors.border} mx={4} />
              </Box>
            </CardHeader>

            <CardHeader py={1} pt={3} pb={2} flexShrink={0}>
              <HStack spacing={4} flexWrap="wrap">
                {access.can_delete && (
                  <HStack spacing={2} minW="250px">
                    <Select
                      placeholder="Bulk actions"
                      size="sm"
                      value={bulkAction}
                      onChange={(e) => {
                        e.stopPropagation()
                        setBulkAction(e.target.value)
                      }}
                    >
                      {activeTab === 'trash' && <option value="restore">Restore</option>}
                      {activeTab === 'trash' && <option value="delete">Delete Permanently</option>}
                      {activeTab === 'all' && <option value="delete">Delete</option>}
                    </Select>
                    <Button size="sm" colorScheme="blue" onClick={handleBulkActionClick}>
                      Apply
                    </Button>
                  </HStack>
                )}

                <Box flex="0 1 auto" minW="250px" maxW="400px">
                  <InputGroup size="sm">
                    <Input
                      ref={searchRef}
                      placeholder="Search..."
                      onKeyDown={handleKeyDown}
                      defaultValue={searchQuery}
                      pr="2.5rem"
                    />
                    <InputRightElement width="2.5rem">
                      <IconButton
                        aria-label="Search"
                        icon={<Search size={16} />}
                        size="sm"
                        h="1.75rem"
                        w="1.75rem"
                        onClick={handleSearch}
                        variant="ghost"
                      />
                    </InputRightElement>
                  </InputGroup>
                </Box>
              </HStack>
            </CardHeader>

            <CardBody
              p={0}
              pt={1}
              pb={4}
              mx={5}
              flex="1"
              overflow="hidden"
              display="flex"
              flexDirection="column"
              minH={0}
              position="relative"
            >
              <Box h="100%" display="flex" flexDirection="column" minH={0}>
                <DataTable
                  ref={tableRef}
                  options={tableOptions}
                  params={tableParams}
                  access={permissions}
                  setSubSub={showSubSubCallback}
                  onOpenModal={(id) => navigate(`/rates-and-expenses/rate-per-client/${id}`)}
                  customRenderers={customRenderers}
                />
              </Box>
            </CardBody>
          </Card>
        </VStack>
      </Box>

      <Modal isOpen={isOpen} onClose={onClose} isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Confirmation</ModalHeader>
          <ModalBody>
            <Text>{modalMessage}</Text>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onClose} isDisabled={isBulkProcessing}>
              Cancel
            </Button>
            <Button colorScheme="blue" onClick={handleConfirmBulkAction} isLoading={isBulkProcessing} loadingText="Processing...">
              Confirm
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </AdminLayout>
  )
}
